import { useEffect, useMemo, useRef } from 'react';
import type { UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { type IntakeResponse } from '../../services/api';
import type {
  KinnserClinicDataResponse,
  KinnserPersonInfo,
} from '../../services/api/types.gen';
import type { IntakeRecord } from '../../store/slices/intakeSlice';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { createNameMatcher } from '../../utils/fuzzyMatch';
import { filterAgencyInsurancesByClinic } from '../../utils/insuranceOptions';
import type { CreatePatientFormData } from './types';
import { resolveClinicKeyFromAgencyClinicId } from '../../utils/clinicKeyUtils';
import { useKinnserClinicDataShared } from './useKinnserClinicDataShared';

interface UseClinicDataOptions {
  agencySystemName: string | undefined;
  /** The agency_clinic_id (UUID) selected in the form. */
  agencyClinicId: string | null | undefined;
  enabled: boolean;
  selectedIntake: IntakeRecord | null;
  setValue: UseFormSetValue<CreatePatientFormData>;
  trigger: UseFormTrigger<CreatePatientFormData>;
  intakeId: string | undefined;
  saveToServer: (updates: Partial<CreatePatientFormData>) => Promise<void>;
}

interface UseClinicDataReturn {
  clinics: KinnserPersonInfo[];
  clinicData: KinnserClinicDataResponse | null;
  loadingClinics: boolean;
  loadingClinicData: boolean;
  insuranceOptions: { name: string; id: string }[];
}

/**
 * Hook for fetching and managing clinic data.
 *
 * The clinic dropdown always uses agency_clinic_id as the option value so
 * clinics without a Kinnser clinic_key still display correctly. When Kinnser
 * is enabled, the clinic_key is resolved from agencyClinics and passed to
 * the Kinnser API for fetching detailed clinic data (physicians, branches, etc.).
 */
export function useClinicData({
  agencySystemName,
  agencyClinicId,
  enabled,
  selectedIntake,
  setValue,
  trigger,
  intakeId,
  saveToServer,
}: UseClinicDataOptions): UseClinicDataReturn {
  const agencyClinics = useStore(state => state.agencyClinics);
  const agencyClinicsLoading = useStore(state => state.agencyClinicsLoading);
  const fetchAgencyClinics = useStore(state => state.fetchAgencyClinics);
  const isKinnserEnabled = useStore(state => state.isKinnserEnabled);

  const { agencyInsurances, agencyInsurancesLoading, fetchAgencyInsurances } =
    useStore(
      useShallow(state => ({
        agencyInsurances: state.agencyInsurances,
        agencyInsurancesLoading: state.agencyInsurancesLoading,
        fetchAgencyInsurances: state.fetchAgencyInsurances,
      }))
    );

  useEffect(() => {
    if (agencyClinics.length === 0 && !agencyClinicsLoading) {
      fetchAgencyClinics();
    }
  }, [agencyClinics.length, agencyClinicsLoading, fetchAgencyClinics]);

  // Resolve clinic_key from the selected agency_clinic_id for Kinnser API calls
  const resolvedClinicKey = useMemo(() => {
    if (!isKinnserEnabled()) return null;
    return resolveClinicKeyFromAgencyClinicId(agencyClinics, agencyClinicId);
  }, [agencyClinics, agencyClinicId, isKinnserEnabled]);

  const {
    clinicData: kinnserClinicData,
    loadingClinicData: kinnserLoadingClinicData,
  } = useKinnserClinicDataShared({
    agencySystemName,
    kinnserClinicId: resolvedClinicKey,
    enabled,
  });

  // Always use agencyClinics for the dropdown, keyed by agency_clinic_id
  const clinicOptions: KinnserPersonInfo[] = useMemo(
    () => agencyClinics.map(ac => ({ name: ac.name, id: ac.id })),
    [agencyClinics]
  );

  const kinnser = isKinnserEnabled();
  const clinicData = kinnser ? kinnserClinicData : null;
  const loadingClinicData = kinnser ? kinnserLoadingClinicData : false;

  // Ensure agency insurances are loaded (needed when clinic has no clinic_key)
  useEffect(() => {
    if (agencyInsurances.length === 0 && !agencyInsurancesLoading) {
      fetchAgencyInsurances();
    }
  }, [agencyInsurances.length, agencyInsurancesLoading, fetchAgencyInsurances]);

  // Build insurance options: prefer Kinnser clinic data, fall back to agency_insurances
  const insuranceOptions = useMemo(() => {
    if (
      clinicData?.insurance_providers &&
      clinicData.insurance_providers.length > 0
    ) {
      return clinicData.insurance_providers;
    }
    if (!agencyClinicId) return [];
    const filtered = filterAgencyInsurancesByClinic(
      agencyInsurances,
      agencyClinicId
    );
    return filtered.map(ai => ({ id: ai.id, name: ai.name }));
  }, [clinicData?.insurance_providers, agencyClinicId, agencyInsurances]);

  // Track previous clinic ID to detect user-initiated changes vs initial load
  const previousClinicIdRef = useRef<string | null | undefined>(undefined);

  // Track if auto-select has been done for this intake
  const autoSelectDoneRef = useRef(false);

  // Reset auto-select flag when intakeId changes
  useEffect(() => {
    autoSelectDoneRef.current = false;
  }, [intakeId]);

  // Auto-select clinic if there's only one and not already set
  useEffect(() => {
    if (autoSelectDoneRef.current) return;
    if (agencyClinicsLoading || clinicOptions.length !== 1) return;

    const intakeData = selectedIntake as unknown as IntakeResponse;
    if (!intakeData?.agency_clinic_id) {
      autoSelectDoneRef.current = true;
      const clinic = agencyClinics[0];
      const clinicId = clinic.id;
      setValue('agency_clinic_id', clinicId);
      saveToServer({
        agency_clinic_id: clinicId,
      } as Partial<CreatePatientFormData>).catch(err => {
        console.error('Failed to auto-save clinic selection:', err);
      });
    }
  }, [
    clinicOptions,
    agencyClinics,
    agencyClinicsLoading,
    selectedIntake,
    setValue,
    saveToServer,
    intakeId,
  ]);

  // Trigger validation after clinic data is loaded
  useEffect(() => {
    if (clinicData && !loadingClinicData) {
      setTimeout(() => trigger(), 0);
    }
  }, [clinicData, loadingClinicData, trigger]);

  // Handle fuzzy matching when clinic data or intake data changes
  useEffect(() => {
    if (!clinicData || !selectedIntake) return;

    const intakeData = selectedIntake as unknown as IntakeResponse;

    const autoMatchedValues: Partial<CreatePatientFormData> = {};

    if (clinicData.branches?.length === 1 && !intakeData?.kinnser_branch_id) {
      setValue('kinnser_branch_id', clinicData.branches[0].id);
      autoMatchedValues.kinnser_branch_id = clinicData.branches[0].id;
    }

    const referringPhysician = selectedIntake?.referring_physician;

    if (
      referringPhysician &&
      clinicData.physicians?.length &&
      !selectedIntake?.kinnser_physician_id
    ) {
      const matcher = createNameMatcher(clinicData.physicians);
      const match = matcher.findBestMatch(referringPhysician);

      if (match) {
        console.log(
          `Auto-matching physician "${referringPhysician}" to "${match.name}"`
        );
        setValue('kinnser_physician_id', match.id);
        autoMatchedValues.kinnser_physician_id = match.id;
      }
    }

    if (Object.keys(autoMatchedValues).length > 0) {
      saveToServer(autoMatchedValues);
    }
  }, [clinicData, selectedIntake, saveToServer, setValue]);

  useEffect(() => {
    if (agencyClinicId && previousClinicIdRef.current === undefined) {
      previousClinicIdRef.current = agencyClinicId;
    } else if (
      agencyClinicId &&
      previousClinicIdRef.current !== agencyClinicId
    ) {
      previousClinicIdRef.current = agencyClinicId;
    }
  }, [agencyClinicId]);

  return {
    clinics: clinicOptions,
    clinicData,
    loadingClinics: agencyClinicsLoading,
    loadingClinicData,
    insuranceOptions,
  };
}
